import { useEffect, useState } from "react";
import { EntriesPerPage } from "../../../../Component/Model/Table/Table";
import { ColumnSortOption } from "../../../../Component/Model/Dto/ColumnSortOption";
import { ImportManagement } from "../../../../Component/Model/Dto/VerificationAgency/Client/ImportManagement/ImportManagement";
import { DataTable } from "../../../../Component/Common/Components/DataTable/DataTable";
import { ImportManagementHistoryTableMapper } from "./ImportManagementHistoryTable";
import { useRecoilValue } from "recoil";
import { BBBEEScorecardAtom } from "../../../../State/BBBEEScorecard.Atom";
import { OrganisationAtom } from "../../../../State/Organisation.Atom";
import { useHistory } from "react-router-dom";
import {
    VerificationWorkflowService
} from "../../../../Component/Api/VerificationAgency/VerificationWorkflow/VerificationWorkflowService";
import { MDTypography } from "../../../../Component/Common/Components/MDTypography";

interface ImportManagementHistoryProps {
    isPayrollImport?: boolean;
}

export const ImportManagementHistory = ({ isPayrollImport = false }: ImportManagementHistoryProps) => {
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(1);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [refreshTable, setRefreshTable] = useState<boolean>(false);
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [importManagementData, setImportManagementData] = useState<ImportManagement.ImportManagementSearchResult[]>([]);
    const scorecard = useRecoilValue(BBBEEScorecardAtom);
    const organisation = useRecoilValue(OrganisationAtom);
    const history = useHistory();

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }
    
    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }
    
    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
    };

    const viewImportDetails = (id: string, isInProgress: string, scorecardId?: string) => {
        if (isPayrollImport) {
            history.push(`/organisation/payrollData/details/${scorecardId}/${id}/${isInProgress}`);
        } else {
            history.push(`/bee/scorecard/${scorecard.scorecardId}/importDetails/${id}/${isInProgress}`);
        }
    }

    const checkForInProgressImport = async () => {
        try {
            setIsLoading(true);
            const result = await VerificationWorkflowService.IsImportInProgressForScorecard(scorecard.scorecardId);
            setRefreshTable(!!result.data);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }
    
    const searchRequest: ImportManagement.HistorySearchRequest = {
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        columnSortOptions: columnSortOptions,
        searchValue: searchValue,
        scorecardId: isPayrollImport ? undefined : scorecard.scorecardId,
        ...(isPayrollImport && { 
            organisationId: organisation.OrganisationId.toString(),
            importSource: 7
        })
    };
    
    const fetchData = async () => {
        setIsLoading(true);
        try {
            const searchResult = await VerificationWorkflowService.ImportManagementHistory(searchRequest);
            setImportManagementData(searchResult.data.results);
            setFilteredItemsCount(searchResult.data.filteredItemsCount);
            setTotalItemsCount(searchResult.data.totalItemsCount);

        } catch (error) {
            setImportManagementData([]);
            setFilteredItemsCount(1);
            setTotalItemsCount(1);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if(!isPayrollImport)
            checkForInProgressImport();
        fetchData();
        if(refreshTable){
            const intervalId = setInterval(() => {
                fetchData();
            }, 60000); 
        
            return () => clearInterval(intervalId);
        }
        
    }, [ entriesPerPage, pageNumber, columnSortOptions, searchValue, refreshTable]);
    
    const tableData = ImportManagementHistoryTableMapper(importManagementData, viewImportDetails);

    return (
        <>
            <MDTypography variant='h4' fontWeight='bold' style={{ paddingBottom: "15px" }}>
                Import History
            </MDTypography>
            <DataTable
                key={totalItemsCount}
                table={tableData}
                canSearch
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                searchValueChanged={searchValueChanged}
                isLoading={isLoading}
                manualSorting={true}
                columnSortingChanged={columnSortingChanged}
            />

        </>
    )
}
